<template>
  <div
    class="texte-view"
    :style="{
      '--paper': theme.paper,
      '--ink': theme.ink,
      '--soft': theme.soft,
      '--accent': theme.accent,
      '--hair': theme.hair,
      '--panel': theme.panel,
      '--panel-soft': theme.panelSoft,
      '--panel-acc': theme.panelAcc,
    }"
  >
    <!-- Reading area -->
    <div class="main-area">
      <div class="reading-zone">
        <div class="nav-row">
          <button class="crumb-btn" @click="$emit('go-recueil')">{{ crumb }}</button>
          <button class="sommaire-btn" @click="$emit('go-sommaire')">sommaire</button>
        </div>

        <div class="illustration" :style="{ background: theme.illu }">
          <img
            v-if="texte.illustrationPath"
            class="illu-img"
            :src="texte.illustrationPath"
            :alt="texte.illustrationCaption"
          />
          <span class="illu-caption">{{ texte.illustrationCaption }}</span>
        </div>

        <div class="texte-title">{{ texte.title }}</div>
        <div class="accent-bar"></div>

        <div class="content-html" v-html="texte.impressionContent"></div>

        <template v-if="texte.noteEntries && texte.noteEntries.length">
          <div class="notes-sep"></div>
          <div class="notes">
            <div v-for="note in texte.noteEntries" :key="note" class="note-entry">{{ note }}</div>
          </div>
        </template>

        <div class="divider"></div>
        <button class="next-btn" @click="$emit('go-next')">suivant</button>
      </div>
    </div>

    <!-- Desktop sidebar -->
    <div class="sidebar">
      <div class="sidebar-name">{{ recueil.name }}</div>
      <div
        v-for="(t, ti) in recueil.texts"
        :key="t.title"
        class="sidebar-row"
        @click="$emit('go-texte', ti)"
      >
        <span class="sidebar-num">{{ pad2(ti) }}</span>
        <span
          class="sidebar-title"
          :style="{ color: ti === texteIndex ? '#F4F6F8' : theme.panelAcc }"
        >{{ t.title }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { RecueilData } from '../types'
import type { ImpressionType } from '@/ImpressionType'
import { THEMES } from '../themes'

const props = defineProps<{
  recueil: RecueilData
  texte: ImpressionType
  texteIndex: number
}>()

defineEmits<{
  'go-sommaire': []
  'go-recueil': []
  'go-next': []
  'go-texte': [ti: number]
}>()

const theme = computed(() => THEMES[props.recueil.key])

const crumb = computed(() => {
  const total = props.recueil.texts.length
  return `← ${props.recueil.name.toLowerCase()} ${pad2(props.texteIndex)}/${pad2(total - 1)}`
})

function pad2(i: number): string {
  return String(i + 1).padStart(2, '0')
}
</script>

<style scoped>
.texte-view {
  display: flex;
  min-height: 100vh;
  background: var(--paper);
}

/* ── Reading area ── */
.main-area {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 34px 30px 0;
}

.reading-zone {
  width: 100%;
  max-width: 330px;
  display: flex;
  flex-direction: column;
}

.nav-row {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  letter-spacing: 0.22em;
}

.crumb-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.22em;
  color: var(--accent);
}

.crumb-btn:hover { opacity: 0.72; }

.sommaire-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.22em;
  color: var(--soft);
}

.sommaire-btn:hover { opacity: 0.72; }

@media (min-width: 1100px) {
  .sommaire-btn { display: none; }
}

.illustration {
  margin-top: 24px;
  aspect-ratio: 33 / 20;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  padding: 13px;
}

.illu-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.illu-caption {
  position: relative;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px;
  letter-spacing: 0.14em;
  color: var(--soft);
}

.texte-title {
  margin-top: 34px;
  font-family: 'Special Elite', monospace;
  font-size: 23px;
  letter-spacing: 0.08em;
  line-height: 1.45;
  text-transform: uppercase;
  color: var(--ink);
}

.accent-bar {
  margin-top: 22px;
  height: 2px;
  width: 48px;
  background: var(--accent);
}

.content-html {
  margin-top: 28px;
}

.content-html :deep(p) {
  font-family: 'Special Elite', monospace;
  font-size: 17px;
  line-height: 2.2;
  color: var(--ink);
  margin: 0;
}

.notes-sep {
  margin-top: 32px;
  height: 1px;
  background: var(--hair);
}

.notes {
  margin-top: 14px;
}

.note-entry {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.1em;
  line-height: 1.8;
  color: var(--soft);
}

.divider {
  margin-top: 44px;
  height: 1px;
  background: var(--hair);
}

.next-btn {
  margin-top: 18px;
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: center;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  padding: 6px 0 40px;
  color: var(--accent);
}

.next-btn:hover { opacity: 0.72; }

/* ── Sidebar (desktop only) ── */
.sidebar {
  display: none;
}

/* ── Breakpoints ── */
@media (min-width: 640px) {
  .main-area { padding: 44px 56px 0; }
  .reading-zone { max-width: 560px; }
  .texte-title { font-size: 27px; }
  .content-html :deep(p) { font-size: 18px; }
}

@media (min-width: 1100px) {
  .main-area { padding: 48px 60px 0; }
  .reading-zone { max-width: 640px; }
  .texte-title { font-size: 30px; }
  .content-html :deep(p) { font-size: 19px; }

  .sidebar {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 280px;
    flex: none;
    overflow: auto;
    padding: 48px 26px 0;
    gap: 4px;
    background: var(--panel);
  }
}

.sidebar-name {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.22em;
  margin-bottom: 16px;
  color: var(--panel-soft);
}

.sidebar-row {
  display: flex;
  gap: 10px;
  align-items: baseline;
  padding: 9px 0;
  cursor: pointer;
  transition: opacity 0.15s;
}

.sidebar-row:hover { opacity: 0.75; }

.sidebar-num {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  width: 20px;
  flex: none;
  color: var(--panel-soft);
}

.sidebar-title {
  font-family: 'Special Elite', monospace;
  font-size: 14px;
  letter-spacing: 0.04em;
  transition: color 0.15s;
}
</style>
